import { reactive, computed, inject, ref, onMounted, watchEffect } from 'vue';
import { Api } from '@/services/api.service'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faClock, faCirclePlay, faCalendarCheck, faUserLock, faDownload, faInfo, faHeart, faEye, faHeartCircleCheck, faHeartCirclePlus, faCircleInfo, faCheck, faRss, faHandPointUp, faXmark, faBookmark } from '@fortawesome/free-solid-svg-icons';
import { faBookmark as regularBookmark } from '@fortawesome/free-regular-svg-icons';
import ProgressBar from '@/components/content/blocks/progress-bar/progress-bar.vue';
import FormattingService from '@/services/formatting.service';
import { faFacebookF, faTwitter, faYoutube, faInstagram,  } from '@fortawesome/free-brands-svg-icons'
import DefaultCollection from '@/components/content/views/default-collection/default-collection.vue';
import CoursePageSvg from '@/components/content/blocks/course-page-svg/course-page-svg.vue';

library.add(faClock);
library.add(faCalendarCheck);
library.add(faCircleInfo);
library.add(faUserLock);
library.add(faDownload);
library.add(faInfo);
library.add(faHeart);
library.add(faEye);
library.add(faCirclePlay);
library.add(faHeartCircleCheck);
library.add(faCheck);
library.add(faFacebookF);
library.add(faTwitter);
library.add(faRss);
library.add(faYoutube);
library.add(faInstagram);
library.add(faHandPointUp);
library.add(faHeartCirclePlus);
library.add(faXmark);
library.add(faBookmark);
library.add(regularBookmark);

export default {
    name: 'CoursePage',
    props: {
      user: {
        type: Object,
        default: {id: null, token: null}
      },
      courseId: {
        type: [Number, String],
        required: true,
      },
      course: {
        type: Object,
        required: false,
      },
      courseOffer: {
        type: Object,
        default: {}
      },
      membershipOffer: {
        type: Object,
        required: false
      },
      allowFirstLesson: {
        type: Boolean,
        default: true,
      },
      disclaimers: {
        type: Object,
        required: true,
      },
      isFreeCourse: {
        type: Boolean,
        default: false,
      },
      tiers: {
        type: Object,
        required: false
      }
    },
    components: {
      FontAwesomeIcon,
      ProgressBar,
      DefaultCollection,
      CoursePageSvg,
    },
    setup(props) {

      let config = inject('config');
      const { api, debug } = config;
      const authProvider = inject('authProvider');
      const memberProvider = inject('memberProvider');

      const headers = new Headers();
      headers.append('Accept', 'application/json');
      headers.append('Content-Type', 'application/json');

      let jwpScript = document.createElement('script');
      jwpScript.setAttribute('src',
        'https://cdn.jwplayer.com/libraries/RwDKnRJN.js');
      document.head.appendChild(jwpScript);

      const coursePage = ref(null);
      const details = ref(null);
      const lessons = ref(null);
      const desktopInstructors = ref(null);
      const mobileInstructors = ref(null);
      const video = ref(null);
      const gear = ref(null);
      const resources = ref(null);
      const similar = ref(null);
      const captions = ref(null);
      const navbar = ref(null);

      const jwplayerConfig = {
        'width': '100%',
        'height': '100%',
        'aspectratio': '16:9',
        'controls': true,
        'stretching': 'uniform',
        'autostart': 'true',
        'mute': false,
        'displaytitle': false,
        'autoPause': {
          'viewability': false,
        }
      };

      const state = reactive({
        loaded: false,
        member: props.user,
        hasUser: false,
        courseId: computed(() => props.courseId),
        course: null,
        activeSection: 'details',
        width: 0,
        top: 0,
        bottom: 0,
        adjustedTop: computed(() => state.hasUser ? state.top - 16 : 0),
        isBookmarked: computed(() => {
          if(state.hasUser) {
            return state.member.bookmarks && state.member.bookmarks.includes(state.course.id);
          }
          return false;
        }),
        totalDuration: computed(() => state.course.lessons.reduce((total, lesson) => total + lesson.duration, 0)),
        memberCompletedLessons: computed(() => authProvider().getCompletedLessons),
        lessons: computed(() => {
          if (!state.course) return [];

          return state.course.lessons.map(lesson => ({
            ...lesson,
            completed: state.memberCompletedLessons.includes(lesson.id)
          }));
        }),
        recommendedCourses: [],
        similarCourses: computed(() => state.recommendedCourses.slice(0, 4)),
        showRecommendations: false,
        splitLessons: computed(() => state.lessons.length > 12),
        showAllLessons: false,
        viewableLessons: computed(() => {
          if(state.splitLessons) {
            return state.showAllLessons ? state.lessons : state.lessons.slice(0, 12);
          }
          return state.lessons;
        }),
        isMobile: computed(() => state.width < 1000),
        showDescription: false,
        partialDescription: computed(() => state.course.content.raw.substring(0, 100)),
        showToolTip: true,
        showCourseImage: true,
        resumeModal: false,
        showPlayButton: computed(() => (state.course.lessons.length > 1 || state.hasUser) && !state.resumeModal ),
        seamlessPlayer: {
          player: null,
          activeIndex: 0,
          position: 0,
          playlist: [],
          autoplay: true,
          options: {
            debounce: true,
          },
          tracking: {
            interval: 15,
          },
          isPlaying: false,
        },
        hasAccess: computed(() => {
          let access = false;
          if(state.hasUser) {
            for (const sub of state.member.subscriptions) {
              if(sub.status === 'active' || sub.status === 'past_due') {
                for (const product of sub.products) {
                  if(state.course.tiers.includes(product.tier)) {
                    access = true;
                  }
                }
              }
            }
          }
          return access;
        }),
        hasArchiveAccess: computed(() => {
          let access = false;
          let memberships = [1060, 1];
          if(state.hasUser) {
            for (const sub of state.member.subscriptions) {
              if(sub.status === 'active' || sub.status === 'past_due') {
                for (const product of sub.products) {
                  if(state.course.tiers.includes(product.tier) && !memberships.includes(product.tier)) {
                    access = true;
                  }
                }
              }
            }
          }
          return access;
        }),
        membershipOffer: props.membershipOffer ? props.membershipOffer : {},
        courseOfferOriginalCost: computed(() => props.courseOffer.product.price_in_cents / 100),
        courseOfferCost: computed(() => calculateOfferCost(props.courseOffer)),
        membershipOfferOriginalCost: computed(() => state.membershipOffer.product.price_in_cents / 100),
        membershipOfferCost: computed(() => calculateOfferCost(state.membershipOffer)),
        courseOfferLink: computed(() => getOfferLink(props.courseOffer)),
        membershipOfferLink: computed(() => getOfferLink(state.membershipOffer)),
        courseOfferSplitCost: computed(() => splitCost(state.courseOfferCost)),
        membershipOfferSplitCost: computed(() => splitCost(state.membershipOfferCost)),
        courseOfferHasDiscount: computed(() => hasDiscount(props.courseOffer)),
        membershipOfferHasDiscount: computed(() => hasDiscount(state.membershipOffer)),
        hasMembershipOffer: computed(() => {
          return Object.keys(state.membershipOffer).length > 0;
        }),
        hasCouresOffer: computed(() => {
          return (!state.course.is_archived && Boolean(props.courseOffer.product));
        }),
        hasBothOffers: computed(() => {
          return state.hasCouresOffer && state.hasMembershipOffer;
        }),
        disclaimer: computed(() => {
          return props.disclaimers.pro;
        }),
        hoverIcon: false,
        favoriteIcon: computed(() => {
          if(state.isBookmarked) {
            if(state.hoverIcon) {
              return ['far', 'bookmark'];
            } else {
              return ['fa', 'bookmark'];
            }
          } else {
            return ['far', 'bookmark'];
          }
        }),
        iconColor: computed(() => {
          if(state.isBookmarked) {
            return 'var(--koc-secondary-color)';
          } else {
            if(state.hoverIcon) {
              return 'var(--koc-secondary-color)';
            } else {
              return 'var(--koc-default-text)';
            }
          }
        }),
        upgradeHtml: '<a href="https://members.kelbyone.com/upgrade" style="font-weight: 700">UPGRADE TO PRO</a> to get access to this class!',
        showTranscript: true,
        expandTranscript: false,
        displayTranscript: computed(() => state.course.lessons[0].captions.text),
        captions: computed(() => {
          const div = document.createElement("div");
          if(state.course.lessons[state.seamlessPlayer.activeIndex].captions && state.course.lessons[state.seamlessPlayer.activeIndex].captions.enhanced) {
            div.innerHTML = state.course.lessons[state.seamlessPlayer.activeIndex].captions.enhanced;
          } else {
            div.innerHTML = '';
          }
          return Array.from(div.children).map(s => s.outerHTML);
        }),
        freeSignupLink: computed(() => {
          let courseURI = `/course/${state.course.slug}`;
          return `${courseURI}?product-handle=free&redirect-to=${encodeURIComponent(courseURI)}`;
        }),
        freeBonusText: 'Our goal is to provide you with the knowledge and tools to create the type of images you’ve always dreamed of. This is why we like to occasionally provide complimentary courses, through our free membership option. Simply sign up for a free membership to get access to this course and more.',
        is_vip_course: computed(() => {
          if (state.course && state.course.tiers) {
            if (state.course.tiers.includes(1060) && !state.course.tiers.includes(1)) {
              return true;
            }
          }

          return false;
        }),
        vipSignupLink: 'https://kelbyone.com/vip',
        vipBonusText: 'This course is exclusive to our VIP members. To access this course, you must first become a VIP member. Spots are limited, so join today or sign up to join our waitlist to be notified when spots open up.',
        allowFirstLesson: computed( () => {return state.is_vip_course ? false : props.allowFirstLesson}),
      });

      const initialize = async () => {
        if (props.user.id && props.user.token) {
          await loadUser();
          await fetchCourse(`&user=${state.member.id}&token=${state.member.token}`);
          let positionUrl = `${api.uri}/${api.version}/users/${state.member.id}/content/${state.course.id}/position?token=${state.member.token}`;
          let positionResponse = await Api.get(positionUrl, headers);
          if(positionResponse.status !== 'error') {
            state.seamlessPlayer.activeIndex = positionResponse.data.data.index;
            state.seamlessPlayer.position = positionResponse.data.data.content.position;
            if (debug) {
              console.log('Course Page Most Recent Lesson(): response data', positionResponse);
            }
          }
          state.loaded = true;
          await fetchRecommendedCourses(`&user=${state.member.id}&token=${state.member.token}`);
        } else {
          await fetchCourse();
          state.loaded = true;
          await fetchRecommendedCourses();
        }
      };

      const loadUser = async () => {
        let user = await authProvider().loadUser(state.member.id, state.member.token, config);
        if(user.status !== 'error') {
          state.hasUser = true;
          state.member = user.data.data
        }
      }

      const setJwplayer = async () => {
        await delay(9);
        state.showCourseImage = false;
        await delay(10);
        if (state.hasAccess) {
          state.seamlessPlayer.player = jwplayer('jwcourseplayer').setup({
            ...jwplayerConfig,
            'playlist': `https://content.jwplatform.com/feeds/${state.course.videoKey}.json`,
          });
          state.seamlessPlayer.player.playlistItem(state.seamlessPlayer.activeIndex);
          setupJwplayerEventListeners(state.seamlessPlayer.player);
        } else {
          jwplayer('jwcourseplayer').setup({
            ...jwplayerConfig,
            'playlist': `https://cdn.jwplayer.com/v2/media/${state.course.lessons[0].videoKey}`,
          });
          state.seamlessPlayer.isPlaying = true;
        }
      };

      const scrollToElement = (element) => {
        window.scrollTo({
          top: element.getBoundingClientRect().top + window.scrollY - state.bottom,
          left: element.getBoundingClientRect().left,
          behavior: 'smooth',
        });
      };

      const sectionElements = {
        details: () => details.value,
        lessons: () => lessons.value,
        instructors: () => state.isMobile ? mobileInstructors.value : desktopInstructors.value,
        similar: () => similar.value,
        gear: () => gear.value,
        resources: () => resources.value,
        video: () => video.value,
      };

      const setActiveSection = (section) => {
        if(section === 'discuss') {
          state.activeSection = section;
          window.open(state.course.discussion, '_blank');
          return;
        }
        if(state.activeSection === section) return;
        state.activeSection = section;
        scrollToDiv(section);
      }

      const toggleAutoplay = () => {
        state.seamlessPlayer.autoplay = !state.seamlessPlayer.autoplay;
      }

      const calculateOfferCost = (offer) => {
        if (!offer || !offer.coupon.discount_type) {
          return offer.product.price_in_cents / 100;
        }
        if (offer.coupon.discount_type === 'percentage') {
          return (offer.product.price_in_cents * (1 - (offer.coupon.discount_amount / 100)) / 100).toFixed(2);
        }
        return (offer.product.price_in_cents - offer.coupon.discount_amount) / 100;
      };

      const handleTimeEvent = (event) => {
        const intPlayedTime = parseInt(event.position, 10);
        if (state.seamlessPlayer.options.debounce) {
          const debounceCheck = intPlayedTime >= Number(parseInt(event.duration, 10) - 3);
          const shouldTrackCheck = shouldTrack(intPlayedTime);
          if (debounceCheck || shouldTrackCheck) {
            state.seamlessPlayer.options.debounce = false;
            trackVideoProgress(intPlayedTime);
            setTimeout(() => {
              state.seamlessPlayer.options.debounce = true;
            }, 1000);
          }
        }
      };

      const handleCaptionEvent = (event) => {
        const position = event.position * 1000;
        captions.value[0].querySelectorAll('[timestamp][end_timestamp]:not(.highlight)').forEach((caption) => {
          if (
            caption.getAttribute('timestamp') < Number(position) &&
            Number(position) < caption.getAttribute('end_timestamp')
          ) {
            captions.value[0].querySelectorAll('[timestamp]').forEach((item) => {
              item.classList.remove('highlight');
            });
            caption.classList.add('highlight');
          }
        });
      }

      const setupJwplayerEventListeners = (player) => {
        player.on('firstFrame', () => {
            setPosition();
          })
          .on('beforeComplete', () => {
            state.seamlessPlayer.position = 0;
          })
          .on('playlistItem', (event) => {
            state.seamlessPlayer.activeIndex = event.index;
            checkAutoplay();
            loadUser();
          })
          .on('playlistComplete', followUpVideo)
          .on('time', (event) => {
            handleTimeEvent(event);
            handleCaptionEvent(event);
          });
      };

      const getOfferLink = (offer) => offer.card.footer.ctas[0].url;

      const splitCost = (offerCost) => offerCost.toString().split(".");

      const hasDiscount = (offer) => !!offer && !!offer.coupon.discount_type;

      const buildUrl = (path, params = '') => `${api.uri}/${api.version}${path}${params ? '?' + params : ''}`;

      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      const fetchCourse = async (urlParams = '') => {
        const url = buildUrl(`/courses/${state.courseId}`, `include=instructors,lessons${urlParams}`);
        const response = await Api.get(url, headers);
        if (response.status !== 'error') {
          state.course = response.data.data;
          if (debug) {
            console.log('Course Page Initialize(): response data', response);
          }
        }
      };

      const fetchRecommendedCourses = async (urlParams = '') => {
        const url = buildUrl(`/courses/${state.courseId}/recommend`, `include=instructors,lessons&per_page=9${urlParams}`);
        const response = await Api.get(url, headers);
        if (response.status !== 'error') {
          state.recommendedCourses = response.data.data;
          if (debug) {
            console.log('Course Page Recommended Courses(): response data', response);
          }
        }
      };

      const scrollToDiv = (section) => {
        const element = sectionElements[section]();
        if (element) {
          scrollToElement(element);
        }
      };

      const toggleBookmark = async () => {
        await memberProvider().toggleBookmark(state.course.id, state.member.id, !state.isBookmarked, state.member.token, config);
        let user = await authProvider().loadUser(state.member.id, state.member.token, config);
        if(user.status !== 'error') {
          state.hasUser = true;
          state.member = user.data.data
        }
        state.hoverIcon = false;
      }

      const toggleLessons = () => {
        state.showAllLessons = !state.showAllLessons;
      }

      const playLesson = async (index, lesson) => {
        if (shouldPlay(index) && !lesson.is_locked) {
          state.seamlessPlayer.activeIndex = index;
          state.seamlessPlayer.position = 0;
          if (!state.seamlessPlayer.player) {
            await setJwplayer();
          } else {
            state.seamlessPlayer.player.playlistItem(index);
          }
          scrollToDiv('video');
        }
      };

      const shouldPlay = (index) => {
        if (state.course.is_archived) {
          return (
            (state.hasArchiveAccess) ||
            (state.lessons.length > 1 && index === 0)
          );
        } else {
          return (
            (state.hasAccess) ||
            (state.hasUser && props.isFreeCourse) ||
            (state.allowFirstLesson && state.lessons.length > 1 && index === 0)
          );
        }
      };

      const toggleDescription = () => {
        state.showDescription = !state.showDescription;
      }

      const formatLink = (link, type) => {
        if(!link) return link;
        if(link.includes('http')) return link;
        return `https://${type}.com/${link}`;
      }

      const trackVideoProgress = async(position) => {
        let activeLesson = state.course.lessons[state.seamlessPlayer.activeIndex];
        let url = `${api.uri}/${api.version}/users/${state.member.id}/content/${activeLesson.id}`;
        let body = {
          token: state.member.token,
          lessonId: activeLesson.id,
          position: position
        }
        await Api.post(url, body, headers);
      }

      const shouldTrack = function(intPlayedTime) {
        if (!state.seamlessPlayer.options.debounce || intPlayedTime === 0) return false;
        return (
          intPlayedTime %
            (state.seamlessPlayer.tracking.interval *
              state.seamlessPlayer.player.getPlaybackRate()) ===
          0
        );
      };

      const checkAutoplay = () => {
        if(state.seamlessPlayer.autoplay) {
          state.seamlessPlayer.player.play();
        } else {
          state.seamlessPlayer.player.stop();
        }
      }

      const setPosition = () => {
        state.seamlessPlayer.player.seek(state.seamlessPlayer.position);
      }

      const play = async () => {
        state.resumeModal = false;
        state.seamlessPlayer.activeIndex = 0;
        state.seamlessPlayer.position = 0;
        await setJwplayer();
        scrollToDiv('video');
      }

      const resume = async () => {
        state.resumeModal = false;
        await setJwplayer();
        scrollToDiv('video');
      }

      const resumeOrPlay = async () => {
        if(!state.hasUser) {
          await setJwplayer();
          scrollToDiv('video');
        } else {
          if(state.seamlessPlayer.activeIndex === 0 && state.seamlessPlayer.position === 0) {
            play();
          } else {
            state.resumeModal = true;
          }
        }
      }

      const followUpVideo = () => {
        setTimeout(() => {
          state.seamlessPlayer.player = jwplayer('jwcourseplayer').setup({
            ...jwplayerConfig,
            'playlist': `https://content.jwplatform.com/feeds/JgAiHTbn.json`,
          }).on('playlistComplete', function(event) {
              showRecommendations();
          });
        }, 0);
      }

      const showRecommendations = () => {
        state.seamlessPlayer.player.remove();
        state.showRecommendations = true;
      }

      const closeModal = () => {
        state.resumeModal = false;
      }

      const onResize = () => {
        state.width = coursePage.value.clientWidth;
        state.top = coursePage.value.getBoundingClientRect().top;
        if(navbar.value) {
          state.bottom = navbar.value.getBoundingClientRect().bottom;
        }
      }

      const toggleTranscript = () => {
        state.showTranscript = !state.showTranscript;
      }

      const toggleExpand = () => {
        state.expandTranscript = !state.expandTranscript;
      }

      const seekFromCaption = (html) => {
        if(!state.seamlessPlayer.player) {
          return;
        }
        const regex = /timestamp="(\d+)"/;
        const match = html.match(regex);
        if(match) {
          let position = match[1];
          state.seamlessPlayer.player.seek(position / 1000);
          state.seamlessPlayer.player.play();
        }
      }

      onMounted(() => {
        window.addEventListener('resize', onResize);
        state.width = coursePage.value.clientWidth;
        state.top = coursePage.value.getBoundingClientRect().top;
      });

      watchEffect(() => {
        if(state.loaded && navbar.value) {
          state.bottom = navbar.value.getBoundingClientRect().bottom;
        }
      })

      initialize();

      return {
        state,
        setActiveSection,
        FormattingService,
        toggleAutoplay,
        toggleBookmark,
        toggleLessons,
        playLesson,
        coursePage,
        toggleDescription,
        formatLink,
        trackVideoProgress,
        resumeOrPlay,
        play,
        resume,
        closeModal,
        scrollToDiv,
        details,
        lessons,
        video,
        mobileInstructors,
        desktopInstructors,
        resources,
        gear,
        similar,
        toggleTranscript,
        toggleExpand,
        seekFromCaption,
        captions,
        navbar,
        shouldPlay
      }
    },
};
